import React, { useEffect, useState } from "react";
import taskStatusService from "../../services/ws/taskStatus";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";

const PLATFORMS = [
  { key: "base", label: "Base" },
  { key: "otto", label: "Otto" },
  { key: "kaufland", label: "Kaufland" },
  { key: "shopify", label: "Shopify" },
  { key: "fbm", label: "FBM" },
  { key: "fba", label: "FBA" },
];

function statusKeyFromTask(taskName) {
  const t = (taskName || "").toLowerCase();
  if (t.includes("sync") || t.includes("do_sync")) return "sync";
  if (
    t.includes("recalc") ||
    t.includes("recalculate") ||
    t.includes("recalculate_articles") ||
    t.includes("monitor") ||
    t.includes("price")
  )
    return "recalc";
  if (t.includes("export")) return "export";
  return null;
}

const Dot = ({ active }) => (
  <span
    style={{
      display: "inline-block",
      width: 12,
      height: 12,
      borderRadius: 12,
      marginLeft: 8,
      background: active ? "#d32f2f" : "#2e7d32",
      boxShadow: active ? "0 0 6px rgba(211,47,47,0.6)" : "none",
    }}
  />
);

const TaskStatusWidget = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [snapshot, setSnapshot] = useState([]);

  useEffect(() => {
    const unsub = taskStatusService.subscribe((s) => setSnapshot(s));
    return () => unsub();
  }, []);

  // build map: platform -> { sync, recalc, export }
  const map = {};
  for (const p of PLATFORMS)
    map[p.key] = { sync: false, recalc: false, export: false };

  for (const msg of snapshot) {
    const source = (msg.source || "otto").toLowerCase();
    const taskKey = statusKeyFromTask(msg.task || msg.name || "");
    if (!taskKey) continue;
    if (!map[source])
      map[source] = { sync: false, recalc: false, export: false };
    map[source][taskKey] = !!msg.active;
  }

  const tooltipText = Object.entries(map)
    .map(
      ([k, v]) =>
        `${k}: sync=${v.sync}, recalc=${v.recalc}, export=${v.export}`,
    )
    .join("\n");

  return (
    <Box sx={{ position: "absolute", right: 16, top: 88 }}>
      <Tooltip
        title={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>}
      >
        <Box
          sx={{
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.96)
              : "rgba(255,255,255,0.9)",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
            p: 1,
            borderRadius: 1,
            boxShadow: isDark ? 4 : 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: "text.primary" }}
          >
            Background jobs
          </Typography>
          {PLATFORMS.map((p) => (
            <Box
              key={p.key}
              sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{ minWidth: 70, color: "text.primary" }}
              >
                {p.label}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                sync
              </Typography>
              <Dot active={map[p.key].sync} />
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                recalc
              </Typography>
              <Dot active={map[p.key].recalc} />
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                export
              </Typography>
              <Dot active={map[p.key].export} />
            </Box>
          ))}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default TaskStatusWidget;
